<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import DeviceEditModal from './DeviceEditModal.vue';

library.add(faPenToSquare);

export default {
  name: 'DeviceSettingsForm',

  components: {
    FontAwesomeIcon,
    DeviceEditModal,
  },

  props: {
    settings: {
      type: Object,
      required: true,
    },

    // 'wifi'/'broadcast'-style section prop, kept for consistency with
    // WifiForm. This form only has one section, so it just controls the
    // heading.
    showHeading: {
      type: Boolean,
      default: false,
    },

    // Whether to offer "load a preset from the MIDI library" in the edit
    // modal — that needs a real path to the internet. web-editor always
    // has one (default true); midulate-editor only does when the device
    // is in station mode, not broadcast/AP mode, so it passes this
    // explicitly based on wifiStatus.
    showLibrarySearch: {
      type: Boolean,
      default: true,
    },

    // Internal MIDI loopback is a capability for someone who already
    // understands how Midulate modulates itself — not a first-run decision.
    // In DeviceSettingsForm it gets its own fieldset and a full paragraph of
    // explanation, which on the onboarding MIDI step competed with the two
    // things that actually matter there, so the wizard hides it (MDL-387).
    // It remains in Settings, unchanged, for when the owner goes looking.
    showLoopback: {
      type: Boolean,
      default: true,
    },

    // Forwarded to DeviceEditModal. Left undefined (no default here) so
    // DeviceEditModal's own default (a relative-URL client, correct for
    // web-editor) applies unless a parent explicitly overrides it —
    // midulate-editor passes an absolute-URL client, since it's served
    // from a different origin than the Rails app hosting the API.
    apiClient: {
      type: Object,
      default: undefined,
    },
  },

  data() {
    return {
      editModalChannel: null, // 1-16, or null when the modal is closed
    };
  },

  watch: {
    // Renaming/reassigning the MIDI channel previously replaced the whole
    // devices[i] object with { name: '' }, silently discarding any cc
    // labels already entered for that slot. Preserve everything else.
    'settings.channel'(newVal) {
      const devices = this.settings.devices;
      if (!Array.isArray(devices)) return;
      // Ignore the transient values a number input produces mid-edit — NaN
      // while the field is empty — so clearing it to retype doesn't drop the
      // label or write to devices[NaN].
      if (!this._isChannel(newVal)) return;
      // Clear by label rather than by the previous index: the old value is
      // undefined for a config that never stored a channel, and indexing with
      // it left the first slot labelled while labelling the new one too, so
      // Midulate appeared on two channels at once (MDL-378). Everything else
      // on the slot, CC labels included, is preserved.
      devices.forEach((device, i) => {
        if (device?.name === 'Midulate') devices[i] = { ...device, name: '' };
      });
      devices[newVal - 1] = { ...devices[newVal - 1], name: 'Midulate' };
    },
  },

  methods: {
    _isChannel(value) {
      return Number.isInteger(value) && value >= 1 && value <= 16;
    },

    ccCount(i) {
      const cc = this.settings.devices[i]?.cc;
      return cc ? Object.keys(cc).length : 0;
    },

    openEditModal(channel) {
      this.editModalChannel = channel;
    },

    closeEditModal() {
      this.editModalChannel = null;
    },
  },
}
</script>
<template>
  <div class="p-4">
    <h2 v-if="showHeading" class="text-2xl font-semibold mb-2">Device Settings</h2>

    <fieldset class="fieldset">
      <legend class="fieldset-legend">MIDI Channel</legend>
      <p class="text-sm text-base-content/60">MIDI Channel to use for controlling Midulate</p>
      <label class="input w-full sm:w-100 mb-2">
        <span class="label w-40">MIDI Channel</span>
        <input type="number" v-model.number="settings.channel" />
      </label>
    </fieldset>

    <template v-if="showLoopback">
      <div class="divider"></div>

      <fieldset class="fieldset">
        <legend class="fieldset-legend">Incoming MIDI</legend>
        <p class="text-sm text-base-content/60">Let Midulate modulate itself without a MIDI loop. When on, an effect's CC output on Midulate's own channel is routed internally to any matching mapping — no cable from MIDI Out back to MIDI In needed.</p>
        <label class="flex items-center gap-3 w-fit">
          <input type="checkbox" class="toggle" v-model="settings.internal_midi_loopback" />
          <span>Internal MIDI loopback</span>
        </label>
      </fieldset>
    </template>

    <div class="divider"></div>

    <fieldset class="fieldset">
      <legend class="fieldset-legend">Devices</legend>
      <p class="text-sm text-base-content/60">Name channels for easier reference, and optionally label the CC numbers each device uses</p>
      <div class="grid sm:grid-cols-2 gap-x-12 pt-4">
        <div v-for="i in 16" :key="i" class="grid grid-cols-[20px_1fr_20px] items-center justify-left gap-2 py-0 min-w-0 cursor-pointer"  @click="openEditModal(i)">
          <div class="text-lg text-base-content/50">{{ i }}</div>
          <div class="text-lg truncate">{{ settings.devices[i - 1].name || '—' }}</div>
          <span
            v-if="ccCount(i - 1) > 0"
            class="badge badge-sm badge-ghost"
            :title="`${ccCount(i - 1)} CC names`"
          >
            {{ ccCount(i - 1) }}
          </span>
        </div>
      </div>
    </fieldset>

    <DeviceEditModal
      :show="editModalChannel !== null"
      :device="editModalChannel !== null ? settings.devices[editModalChannel - 1] : null"
      :channel="editModalChannel"
      :is-own-channel="editModalChannel === settings.channel"
      :show-library-search="showLibrarySearch"
      :api-client="apiClient"
      @close="closeEditModal"
    />
  </div>
</template>
